// ── Provider Constraints page ───────────────────────────────────────────────
// Iter 33d — Now hosts three sections:
//   1. Provider Settings (default teacher domain for the Add Student modal)
//   2. Provider Availability (weekday hours + within-day blocked times)
//   3. Provider Block Schedule (testing / meetings / planning / other recurring
//      blocks with date-range + weeks-of-month support — visible across
//      Today / Week / Calendar and used as hard blocks in the wizard)
import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FloppyDisk, Gear } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProviderAvailabilityCard from "./ProviderAvailabilityCard";
import ProviderPersonalBlocksCard from "./ProviderPersonalBlocksCard";

export default function ProviderConstraints() {
  const [provider, setProvider] = useState(null);

  const loadProvider = async () => {
    try {
      const r = await api.getCurrentProvider();
      setProvider(r.data);
    } catch (e) {
      toast.error(`Failed to load provider: ${e.message}`);
    }
  };
  useEffect(() => {
    loadProvider();
  }, []);

  return (
    <div className="space-y-5" data-testid="provider-constraints">
      <ProviderSettingsCard provider={provider} onSaved={loadProvider} />
      <ProviderAvailabilityCard />
      <ProviderPersonalBlocksCard provider={provider} />
    </div>
  );
}

function ProviderSettingsCard({ provider, onSaved }) {
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!provider) return;
    const derived = provider.email?.includes("@") ? provider.email.split("@", 2)[1] : "";
    setDomain(provider.default_teacher_domain ?? derived ?? "");
  }, [provider]);
  if (!provider) return null;
  const derivedDomain = provider.email?.includes("@") ? provider.email.split("@", 2)[1] : "";
  const save = async () => {
    setSaving(true);
    try {
      await api.updateProviderSettings(provider.id, {
        default_teacher_domain: domain.trim() || null,
      });
      toast.success("Settings saved");
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
      data-testid="provider-settings-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
          <Gear size={16} weight="duotone" className="text-[#4A6B8C]" />
          Provider Settings
        </CardTitle>
        <p className="text-[11px] text-[#6B7280] mt-1">
          These apply to just you. Default teacher domain pre-fills the Add Student teacher-email field so you only type the local part.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
              Default teacher email domain
            </p>
            <div className="flex items-stretch max-w-md">
              <span className="inline-flex items-center px-2 text-xs text-[#9B9890] bg-[#F3F1EC] border border-r-0 border-[#d4d2cc] rounded-l-lg whitespace-nowrap">
                @
              </span>
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder={derivedDomain || "e.g. lincoln.k12.us"}
                data-testid="provider-settings-domain"
                className="h-9 text-sm rounded-l-none"
              />
            </div>
            <p className="text-[10px] text-[#9B9890] mt-1">
              {provider.default_teacher_domain
                ? `Overrides the auto-derived domain${derivedDomain ? ` (${derivedDomain})` : ""}.`
                : derivedDomain
                ? `Derived from your email: @${derivedDomain}`
                : "Enter your school's teacher email domain."}
            </p>
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            data-testid="provider-settings-save"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-xs"
          >
            <FloppyDisk size={12} weight="bold" className="mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
